import React, { useState, useRef, useEffect } from 'react';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ConversationHeader from './ConversationHeader';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Message type definition
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: Date;
  metadata?: {
    tags?: string[];
    status?: 'active' | 'archived' | 'deleted';
    source?: string;
  };
}

interface MessagingTabProps {
  userName: string;
  hideTabsWhenDetail?: (hide: boolean) => void;
  shouldStartNewConversation: boolean;
  initialMessage?: string | null;
  onConversationStarted: () => void;
  conversations: Conversation[];
  activeConversation: string | null;
  setActiveConversation: (id: string | null) => void;
  updateConversation: (conversationId: string, messages: Message[]) => void;
  createNewConversation: (initialMessage: string, messages: Message[]) => string | null;
  sendMessageToWebhook: (message: string) => Promise<{role: string; content: string}>;
}

const MessagingTab: React.FC<MessagingTabProps> = ({
  userName,
  hideTabsWhenDetail,
  shouldStartNewConversation,
  initialMessage,
  onConversationStarted,
  conversations,
  activeConversation,
  setActiveConversation,
  updateConversation,
  createNewConversation,
  sendMessageToWebhook
}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isThinking, setIsThinking] = useState(false);
  const messageInputRef = useRef<{ focus: () => void }>(null);
  const { toast } = useToast();

  // Initialize with welcome message if new conversation
  useEffect(() => {
    if (shouldStartNewConversation) {
      if (initialMessage) {
        // Start with bot greeting and automatically send the initial message
        const botGreeting: Message = {
          id: '1',
          text: t('chatbot.greetings.hiWithQuestion', { userName, message: initialMessage }),
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages([botGreeting]);
        setShowConversationList(false);
        onConversationStarted();
        
        // Automatically send the initial message after a short delay
        setTimeout(() => {
          handleSendMessage(initialMessage);
        }, 500);
      } else {
        // Reset messages to welcome message
        setMessages([
          {
            id: '1',
            text: t('chatbot.greetings.hiGeneral', { userName }),
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
        setShowConversationList(false);
        onConversationStarted();
        // Focus the message input
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }
    }
  }, [shouldStartNewConversation, initialMessage, userName, onConversationStarted, t]);
  
  // Load conversation if activeConversation changes
  useEffect(() => {
    if (activeConversation) {
      const conversation = conversations.find(c => c.id === activeConversation);
      if (conversation) {
        setMessages(conversation.messages);
        setShowConversationList(false);
      }
    } else if (!shouldStartNewConversation) {
      // Show conversation list if no active conversation and not starting new
      setShowConversationList(true);
    }
  }, [activeConversation, conversations, shouldStartNewConversation]);
  
  // Handle sending a message
  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: crypto.randomUUID(),
      text,
      sender: 'user',
      timestamp: new Date()
    };
    
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsThinking(true);
    
    try {
      // Call webhook to get bot response
      const response = await sendMessageToWebhook(text);
      
      // Add bot response
      const botMessage: Message = {
        id: crypto.randomUUID(),
        text: response.content,
        sender: 'bot',
        timestamp: new Date()
      };
      
      const finalMessages = [...updatedMessages, botMessage];
      setMessages(finalMessages);
      
      // Update or create conversation
      if (activeConversation) {
        updateConversation(activeConversation, finalMessages);
      } else {
        const newConversationId = createNewConversation(text, finalMessages);
        if (typeof newConversationId === 'string') {
          setActiveConversation(newConversationId);
        }
      }
    } catch (error) {
      console.error('Error getting response:', error);
      toast({
        title: t('chatbot.errors.error'),
        description: t('chatbot.errors.failedToSend'),
        variant: "destructive",
      });
    } finally {
      setIsThinking(false);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    console.log('Selecting conversation:', conversation.id);
    setMessages(conversation.messages);
    setActiveConversation(conversation.id);
    setShowConversationList(false);
  };

  const handleBack = () => {
    setShowConversationList(true);
    if (hideTabsWhenDetail) {
      hideTabsWhenDetail(false);
    }
  };

  const handleStartNewConversation = () => {
    setShowConversationList(false);
    setMessages([
      {
        id: '1',
        text: t('chatbot.greetings.hiGeneral', { userName }),
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
    setActiveConversation(null);
    if (hideTabsWhenDetail) {
      hideTabsWhenDetail(true);
    }
  };

  return (
    <div className="flex flex-col h-full pb-16">
      {showConversationList ? (
        // Conversation List View
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            <ConversationList
              conversations={conversations}
              onSelectConversation={handleSelectConversation}
              onStartNew={handleStartNewConversation}
            />
          </div>
        </div>
      ) : (
        // Individual Conversation View
        <>
          {/* Back button */}
          <div className="border-b p-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('chatbot.allConversations')}
            </Button>
          </div>
          
          {/* Conversation messages */}
          <div className="flex-1 flex flex-col min-h-0">
            <MessageList 
              messages={messages}
              isThinking={isThinking}
            />
            <MessageInput 
              ref={messageInputRef}
              onSendMessage={handleSendMessage}
              disabled={isThinking}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MessagingTab; 